import { useState } from 'react';
import { flushSync } from 'react-dom';
import { Link } from 'react-router-dom';

import { SEO } from '@platform/seo';

import { useAuth } from '@/features/auth';
import { EXTERNAL_URL, PATHS } from '@/shared/constants/app/routes.constant.ts';
import { buildSeo } from '@/shared/seo/seoBuilder';

import { useDocPrefetch, useDocsTree } from '../api/use-docs.query';
import { DocsPlaylistCard } from '../components/DocsPlaylistCard';
import { DocsSectionIcon } from '../components/DocsSectionIcon';
import { DocsSearchDialog } from '../components/DocsSearchDialog';
import { DocsSearchTrigger } from '../components/DocsSearchTrigger';
import { useDocsSearch } from '../hooks/useDocsSearch';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';
import { firstDocOf, indexDocOf } from '../lib/docsNavigation';

/**
 * "주제별로 찾기" 칸 수 — 3열 세 줄. 섹션 순서대로 앞에서 이만큼만 보인다.
 * 빠진 섹션도 공개는 그대로라 사이드바·검색·직접 링크로 들어갈 수 있다.
 */
const TOPIC_LIMIT = 9;

/** 관리 진입점을 보여줄 권한. 서버도 같은 기준(ADMIN 이상)으로 막는다. */
const MANAGER_ROLES = ['ADMIN', 'SUPER_ADMIN'];

const SECTION_TITLE =
  'mb-4 text-[17px] font-extrabold text-neutral-900 dark:text-neutral-100';

/** "영상으로 배우기" 재생목록. 썸네일은 각 재생목록의 첫 영상이다(바뀌면 여기도 바꾼다). */
const PLAYLISTS = [
  {
    title: 'XCOST 통합내역',
    description: '내역의 꽃, 통합내역을 처음부터.',
    href: EXTERNAL_URL.PRO_YOUTUBE,
    thumbnailVideoId: 'FeNAXSYWbrU',
  },
  {
    title: 'XPIXELS 마감산출',
    description: '마감산출의 숨겨진 기능까지.',
    href: EXTERNAL_URL.AQC_YOUTUBE,
    thumbnailVideoId: 'Rb7w80eVmwc',
  },
  {
    title: 'XPIXELS 구조산출',
    description: '구조산출 자동화 기능 모음.',
    href: EXTERNAL_URL.SQC_YOUTUBE,
    thumbnailVideoId: 'EVVPWABmW1s',
  },
] as const;

/** 사이드바에 묻히면 안 되는 첫 화면. 검색 + 주제별로 찾기 + 영상으로 배우기. */
export function DocsIndexView() {
  const { user } = useAuth();
  const canManage = !!user?.userRole && MANAGER_ROLES.includes(user.userRole);
  const { open, openSearch, closeSearch } = useDocsSearch();
  const { data: tree, isPending } = useDocsTree();
  const prefetchProps = useDocPrefetch();
  // 재생 중인 재생목록 하나. 다른 카드를 누르면 앞의 재생은 멈춘다(iframe 이 빠진다).
  const [playingHref, setPlayingHref] = useState<string | null>(null);
  const playing = PLAYLISTS.find((playlist) => playlist.href === playingHref);
  const reducedMotion = usePrefersReducedMotion();

  const play = (href: string | null) => {
    if (reducedMotion || !document.startViewTransition) {
      setPlayingHref(href);
      return;
    }
    // 전환 콜백 안에서 DOM 이 바로 바뀌어야 새 자리를 찍을 수 있다 — flushSync.
    document.startViewTransition(() => flushSync(() => setPlayingHref(href)));
  };
  const topics = (tree?.sections ?? [])
    .filter((section) => section.showOnIndex)
    .slice(0, TOPIC_LIMIT);

  const seo = buildSeo({
    title: '가이드',
    description:
      '가입부터 원가계산서 출력까지, HOWCOST 사용에 필요한 모든 문서를 한곳에서 봅니다.',
    path: PATHS.DOCS,
  });

  return (
    <>
      <SEO {...seo} />

      <section className="border-neutral-200 bg-linear-to-b from-brand-50 to-white pt-16 pb-5 dark:border-white/12 dark:from-brand-500/8 dark:to-transparent">
        <div className="mx-auto max-w-320 px-5 sm:px-12">
          <p className="mb-1 text-base text-sm text-brand-600">
            이용 가이드
          </p>
          <h1 className="text-3xl font-extrabold tracking-tight text-neutral-900 sm:text-[38px] dark:text-neutral-100">
            무엇을 찾고 계신가요?
          </h1>
          <div className="mt-6 flex max-w-200 flex-wrap items-center gap-3">
            <div className="min-w-64 flex-1">
              <DocsSearchTrigger onClick={openSearch} size="lg" />
            </div>
            {canManage && (
              <Link
                to={PATHS.DOCS_MANAGE}
                className="rounded-lg border border-brand-200 bg-white px-4 py-3.5 text-[14px] font-bold text-brand-700 dark:border-brand-500/30 dark:bg-white/5 dark:text-brand-300"
              >
                도움말 관리
              </Link>
            )}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-320 px-5 py-10 sm:px-12">
        <section>
          <h2 className={SECTION_TITLE}>주제별로 찾기</h2>
          {/* 칸 사이 선은 gap-px 로 배경을 비춰 그린다 — 3열이 9칸을 꽉 채워 빈칸이 없다.
              설명은 넣지 않는다: 주제 이름만으로 고르게 하고, 자세한 건 들어가서 본다. */}
          <ul className="grid gap-px overflow-hidden rounded-xl border border-neutral-200 bg-neutral-200 sm:grid-cols-3 dark:border-white/12 dark:bg-white/12">
            {isPending
              ? Array.from({ length: TOPIC_LIMIT }, (_, i) => (
                  <li
                    key={i}
                    aria-hidden
                    className="h-15 bg-white dark:bg-dark-base"
                  />
                ))
              : topics.map((section) => {
                  // 대표 문서가 있으면 그 문서, 없으면 첫 문서가 도착지다
                  // (`/docs/<section>` 진입 규칙과 같다).
                  const target = indexDocOf(section) ?? firstDocOf(section);
                  return (
                    <li key={section.id} className="bg-white dark:bg-dark-base">
                      {target && (
                        <Link
                          to={target.path}
                          {...prefetchProps(section.slug, target.slug)}
                          className="group flex h-15 items-center gap-3 px-5 text-[15px] font-semibold text-neutral-800 transition-colors hover:bg-brand-50/60 hover:text-brand-700 dark:text-neutral-100 dark:hover:bg-brand-500/8 dark:hover:text-brand-300"
                        >
                          <DocsSectionIcon
                            section={section}
                            className="h-4.5 w-4.5 shrink-0"
                          />
                          <span className="truncate">{section.title}</span>
                        </Link>
                      )}
                    </li>
                  );
                })}
          </ul>
        </section>

        <section className="mt-12">
          <h2 className={SECTION_TITLE}>
            영상으로 배우기
          </h2>
          {playing ? (
            <div className="grid items-start gap-6 lg:grid-cols-[7fr_3fr]">
              <DocsPlaylistCard
                {...playing}
                playing
                onPlay={() => undefined}
                onClose={() => play(null)}
              />
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-1">
                {PLAYLISTS.filter((p) => p.href !== playing.href).map(
                  (playlist) => (
                    <DocsPlaylistCard
                      key={playlist.href}
                      {...playlist}
                      compact
                      playing={false}
                      onPlay={() => play(playlist.href)}
                      onClose={() => play(null)}
                    />
                  ),
                )}
              </div>
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-3">
              {PLAYLISTS.map((playlist) => (
                <DocsPlaylistCard
                  key={playlist.href}
                  {...playlist}
                  playing={false}
                  onPlay={() => play(playlist.href)}
                  onClose={() => play(null)}
                />
              ))}
            </div>
          )}
        </section>
      </div>

      {open && <DocsSearchDialog onClose={closeSearch} />}
    </>
  );
}
